import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'auth/AuthProvider';
import { PERMISSIONS } from 'auth/permissions';
import { queryKeys } from 'core/api/queryKeys';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError, mapFieldErrors } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { SUPPORT_TICKET_STATUSES, operationsApi } from 'features/operations/operations.api';
import { listItems, listMeta } from 'features/operations/operations.helpers';
import { Button } from 'shared/components/Button';
import { Badge, EmptyState, ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { detailsAction, RowActionsCell, RowActionsHeader, RowActionsMenu } from 'shared/components/RowActionsMenu';
import { useConfirm } from 'shared/components/ConfirmDialog';
import { Tabs } from 'shared/components/Tabs';
import { useToast } from 'shared/components/Toast';
import { Pagination } from 'shared/components/Pagination';
import { BilingualField, SwitchField, TextField } from 'shared/forms/Fields';
import { formatDateTime, formatUserLabel } from 'shared/utilities/format';

const emptyCategory = { key: '', title_ar: '', title_en: '', is_active: true, sort_order: '1' };

export function SupportPage() {
  const { t, locale, dir } = useI18n();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.supportManage);
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('tickets');
  const [searchParams, setSearchParams] = useSearchParams();
  const [categoryForm, setCategoryForm] = useState(emptyCategory);
  const [editingId, setEditingId] = useState(null);
  const [categoryErrors, setCategoryErrors] = useState({});

  const filters = useMemo(() => ({
    page: Number(searchParams.get('page') || 1),
    per_page: 20,
    reference: searchParams.get('reference') || '',
    user_id: searchParams.get('user_id') || '',
    category_id: searchParams.get('category_id') || '',
    status: searchParams.get('status') || '',
  }), [searchParams]);

  const categoriesQuery = useQuery({
    queryKey: queryKeys.supportCategories,
    queryFn: ({ signal }) => operationsApi.listSupportCategories({ signal }),
  });
  const ticketsQuery = useQuery({
    queryKey: queryKeys.supportTickets(filters),
    queryFn: ({ signal }) => operationsApi.listSupportTickets(filters, { signal }),
  });

  const saveCategory = useMutation({
    mutationFn: (body) => (editingId
      ? operationsApi.updateSupportCategory(editingId, body)
      : operationsApi.createSupportCategory(body)),
    onSuccess: () => {
      setCategoryForm(emptyCategory);
      setEditingId(null);
      setCategoryErrors({});
      queryClient.invalidateQueries({ queryKey: queryKeys.supportCategories });
      toast.success(t('status.saved'));
    },
    onError: (error) => {
      if (error instanceof ApiError && error.isValidation()) {
        setCategoryErrors(mapFieldErrors(error.errors));
      }
      toast.error(humanizeApiError(error, t));
    },
  });

  const deleteCategory = useMutation({
    mutationFn: (id) => operationsApi.deleteSupportCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.supportCategories });
      toast.success(t('status.deleted'));
    },
    onError: (error) => toast.error(humanizeApiError(error, t)),
  });

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.set('page', '1');
    setSearchParams(next);
  };

  const categories = listItems(categoriesQuery.data);
  const tickets = listItems(ticketsQuery.data);
  const meta = listMeta(ticketsQuery.data);

  return (
    <div>
      <PageHeader title={t('support.title')} />
      <Tabs
        tabs={[
          { id: 'tickets', label: t('support.tickets') },
          { id: 'categories', label: t('support.categories') },
        ]}
        value={tab}
        onChange={setTab}
        label={t('support.title')}
        dir={dir}
      />

      {tab === 'categories' ? (
        <div className="stack-form">
          {canManage ? (
            <form
              className="card stack-form"
              onSubmit={(event) => {
                event.preventDefault();
                saveCategory.mutate({
                  key: categoryForm.key,
                  title_ar: categoryForm.title_ar,
                  title_en: categoryForm.title_en,
                  is_active: categoryForm.is_active,
                  sort_order: Number(categoryForm.sort_order) || 1,
                });
              }}
            >
              <h2>{editingId ? t('support.editCategory') : t('support.createCategory')}</h2>
              <TextField id="category-key" label={t('support.key')} value={categoryForm.key} onChange={(value) => setCategoryForm((current) => ({ ...current, key: value }))} error={categoryErrors.key?.[0]} disabled={Boolean(editingId)} />
              <BilingualField
                id="category-title"
                labelAr={t('support.titleAr')}
                labelEn={t('support.titleEn')}
                valueAr={categoryForm.title_ar}
                valueEn={categoryForm.title_en}
                onChangeAr={(value) => setCategoryForm((current) => ({ ...current, title_ar: value }))}
                onChangeEn={(value) => setCategoryForm((current) => ({ ...current, title_en: value }))}
                errorAr={categoryErrors.title_ar?.[0]}
                errorEn={categoryErrors.title_en?.[0]}
              />
              <TextField id="category-order" label={t('support.sortOrder')} type="number" value={categoryForm.sort_order} onChange={(value) => setCategoryForm((current) => ({ ...current, sort_order: value }))} />
              <SwitchField id="category-active" label={t('support.active')} checked={categoryForm.is_active} onChange={(value) => setCategoryForm((current) => ({ ...current, is_active: value }))} />
              <Button type="submit" loading={saveCategory.isPending}>{t('actions.save')}</Button>
            </form>
          ) : null}
          {categoriesQuery.isLoading ? <Skeleton rows={4} /> : null}
          {categoriesQuery.isError ? <ErrorState title={humanizeApiError(categoriesQuery.error, t)} onRetry={categoriesQuery.refetch} retryLabel={t('actions.retry')} /> : null}
          {categories.length === 0 && !categoriesQuery.isLoading ? <EmptyState title={t('empty.supportCategories')} /> : (
            <div className="table-wrap">
            <table className="data-table has-sticky-actions">
              <thead>
                <tr>
                  <th>{t('support.key')}</th>
                  <th>{t('support.titleAr')}</th>
                  <th>{t('support.titleEn')}</th>
                  <th>{t('support.active')}</th>
                  {canManage ? <RowActionsHeader /> : null}
                </tr>
              </thead>
              <tbody>
                {categories.map((category) => (
                  <tr key={category.id}>
                    <td>{category.key}</td>
                    <td>{category.title_ar}</td>
                    <td>{category.title_en}</td>
                    <td>{category.is_active ? t('support.active') : t('filters.inactive')}</td>
                    {canManage ? (
                      <RowActionsCell
                        label={`${t('actions.rowActions')} #${category.id}`}
                        items={[
                          {
                            key: 'edit',
                            label: t('actions.edit'),
                            onClick: () => {
                              setEditingId(category.id);
                              setCategoryForm({
                                key: category.key || '',
                                title_ar: category.title_ar || '',
                                title_en: category.title_en || '',
                                is_active: category.is_active !== false,
                                sort_order: String(category.sort_order || 1),
                              });
                            },
                          },
                          {
                            key: 'delete',
                            label: t('actions.delete'),
                            tone: 'danger',
                            onClick: async () => {
                              if (await confirm({ body: t('confirm.delete') })) {
                                deleteCategory.mutate(category.id);
                              }
                            },
                          },
                        ]}
                      />
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="filter-grid">
            <input className="input" value={filters.reference} onChange={(event) => setFilter('reference', event.target.value)} placeholder={t('support.reference')} aria-label={t('support.reference')} />
            <input className="input" value={filters.user_id} onChange={(event) => setFilter('user_id', event.target.value)} placeholder={t('support.user')} aria-label={t('support.user')} />
            <select className="input" value={filters.category_id} onChange={(event) => setFilter('category_id', event.target.value)} aria-label={t('support.category')}>
              <option value="">{t('filters.all')}</option>
              {categories.map((category) => <option key={category.id} value={category.id}>{locale === 'ar' ? category.title_ar : category.title_en}</option>)}
            </select>
            <select className="input" value={filters.status} onChange={(event) => setFilter('status', event.target.value)} aria-label={t('support.statuses.open')}>
              <option value="">{t('filters.all')}</option>
              {SUPPORT_TICKET_STATUSES.map((status) => <option key={status} value={status}>{t(`support.statuses.${status}`)}</option>)}
            </select>
            <button type="button" className="btn btn-secondary" onClick={() => setSearchParams({})}>{t('actions.reset')}</button>
          </div>
          {ticketsQuery.isLoading ? <Skeleton rows={6} /> : null}
          {ticketsQuery.isError ? <ErrorState title={humanizeApiError(ticketsQuery.error, t)} onRetry={ticketsQuery.refetch} retryLabel={t('actions.retry')} /> : null}
          {!ticketsQuery.isLoading && tickets.length === 0 ? <EmptyState title={t('empty.supportTickets')} /> : (
            <>
            <div className="table-wrap desktop-table">
            <table className="data-table has-sticky-actions">
              <caption className="sr-only">{t('support.tickets')}</caption>
              <thead>
                <tr>
                  <th>{t('support.reference')}</th>
                  <th>{t('support.user')}</th>
                  <th>{t('support.category')}</th>
                  <th>{t('consultations.status')}</th>
                  <th>{t('consultations.createdFrom')}</th>
                  <RowActionsHeader />
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => (
                  <tr key={ticket.id}>
                    <td>{ticket.reference || ticket.id}</td>
                    <td>{formatUserLabel(ticket.user, t)}</td>
                    <td>{locale === 'ar' ? ticket.category?.title_ar : ticket.category?.title_en}</td>
                    <td><Badge>{t(`support.statuses.${ticket.status}`)}</Badge></td>
                    <td>{formatDateTime(ticket.created_at, locale)}</td>
                    <RowActionsCell
                      label={`${t('actions.rowActions')} #${ticket.id}`}
                      items={[detailsAction(paths.supportTicketDetail(ticket.id), t)]}
                    />
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <ul className="mobile-card-list">
              {tickets.map((ticket) => (
                <li key={`card-${ticket.id}`} className="card">
                  <div className="toolbar wrap">
                    <strong>{ticket.reference || ticket.id}</strong>
                    <RowActionsMenu
                      label={`${t('actions.rowActions')} #${ticket.id}`}
                      items={[detailsAction(paths.supportTicketDetail(ticket.id), t)]}
                    />
                  </div>
                  <p>{formatUserLabel(ticket.user, t)}</p>
                  <Badge>{t(`support.statuses.${ticket.status}`)}</Badge>
                  <p className="muted">{formatDateTime(ticket.created_at, locale)}</p>
                </li>
              ))}
            </ul>
            </>
          )}
          <Pagination
            page={meta.current_page || filters.page}
            lastPage={meta.last_page}
            total={meta.total}
            onPageChange={(page) => setFilter('page', String(page))}
            label={t('pagination.page')}
            ofLabel={t('pagination.of')}
            previousLabel={t('pagination.previous')}
            nextLabel={t('pagination.next')}
            dir={dir}
          />
        </div>
      )}
    </div>
  );
}
